import React, { useEffect, useState } from "react";
import { UserPlus, ShieldAlert, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { FileUpload } from "./FileUpload";

const EMPTY = { member_id: "", benefit_id: "", reason: "", amount_requested: "", documents: [], override_standing: false, override_reason: "" };

const EligibilityBanner = ({ e }) => {
  if (!e) return null;
  const ok = e.welfare_eligible;
  return (
    <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${ok ? "bg-palm/10 text-palm" : "bg-amber-100 text-amber-900"}`} data-testid="obo-eligibility">
      {ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
      <div>
        <p className="font-semibold">{e.name}: {e.status}{e.grandfathered ? " · Grandfathered" : ""}</p>
        {!ok && <ul className="mt-1 list-disc pl-4 text-xs">{(e.outstanding || []).map((o) => <li key={o}>{o}</li>)}</ul>}
      </div>
    </div>
  );
};

function useOnBehalfClaim(onSubmitted) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [elig, setElig] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!form.member_id) return setElig(null);
    api.get(`/admin/welfare/eligibility/${form.member_id}`).then((r) => setElig(r.data)).catch(() => setElig(null));
  }, [form.member_id]);
  const blocked = elig && !elig.welfare_eligible && !(elig.can_override && form.override_standing && form.override_reason.trim());
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/admin/welfare/claims/on-behalf", { ...form, amount_requested: parseFloat(form.amount_requested) || 0 });
      toast.success("Claim submitted on the member's behalf"); setOpen(false); setForm(EMPTY); onSubmitted?.();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const canSubmit = !busy && form.member_id && form.benefit_id && form.reason && !blocked;
  return { open, setOpen, form, setForm, elig, busy, submit, canSubmit };
}

const OverrideBox = ({ elig, form, setForm }) => {
  if (!elig || elig.welfare_eligible || !elig.can_override) return null;
  return (
    <div className="space-y-2 rounded-lg border border-crimson/30 bg-crimson/5 p-3" data-testid="obo-override-box">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1 text-crimson"><ShieldAlert size={14} /> Admin override — submit despite standing</Label>
        <Switch checked={form.override_standing} onCheckedChange={(v) => setForm({ ...form, override_standing: v })} data-testid="obo-override" />
      </div>
      {form.override_standing && <Input placeholder="Reason for override (recorded in audit log)" value={form.override_reason} onChange={(e) => setForm({ ...form, override_reason: e.target.value })} data-testid="obo-override-reason" />}
    </div>
  );
};

const PickerField = ({ label, value, onChange, items, render, placeholder, testId }) => (
  <div><Label>{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{(items || []).map((i) => <SelectItem key={i.id} value={i.id}>{render(i)}</SelectItem>)}</SelectContent>
    </Select>
  </div>
);

const ClaimDetails = ({ form, setForm, benefits }) => (
  <>
    <PickerField label="Benefit" value={form.benefit_id} onChange={(v) => setForm({ ...form, benefit_id: v })} items={benefits} render={(b) => `${b.name} ($${b.amount})`} placeholder="Select benefit" testId="obo-benefit" />
    <div><Label>Reason</Label><Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="obo-reason" /></div>
    <div><Label>Amount requested (blank = benefit default)</Label><Input type="number" value={form.amount_requested} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} data-testid="obo-amount" /></div>
    <div><Label>Supporting documents</Label>
      <FileUpload purpose="welfare_document" testId="obo-doc-upload" onUploaded={(f) => setForm((s) => ({ ...s, documents: [...s.documents, f] }))} />
      {form.documents.length > 0 && <ul className="mt-1 text-xs text-palm">{form.documents.map((d) => <li key={d.id}>• {d.filename}</li>)}</ul>}
    </div>
  </>
);

export const OnBehalfClaim = ({ onSubmitted }) => {
  const { open, setOpen, form, setForm, elig, busy, submit, canSubmit } = useOnBehalfClaim(onSubmitted);
  const { data: members } = useApi("/admin/members");
  const { data: benefits } = useApi("/welfare/benefits");
  return (
    <>
      <Button onClick={() => setOpen(true)} className="gap-2 bg-palm hover:bg-palm/90" data-testid="obo-open-btn"><UserPlus size={15} /> Submit claim for a member</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="obo-dialog">
          <DialogHeader><DialogTitle className="font-serif-display text-crimson">Submit welfare claim on behalf of a member</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <PickerField label="Member" value={form.member_id} onChange={(v) => setForm({ ...form, member_id: v })} items={members} render={(m) => `${m.name} · ${m.member_id}`} placeholder="Select member" testId="obo-member" />
            <EligibilityBanner e={elig} />
            <OverrideBox elig={elig} form={form} setForm={setForm} />
            <ClaimDetails form={form} setForm={setForm} benefits={benefits} />
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={!canSubmit} className="bg-crimson" data-testid="obo-submit">{busy ? "Submitting…" : "Submit claim"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
